import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Settings, Loader2, Check, AlertCircle, 
  Download, Lightbulb, Bookmark, Shield, Mail,
  Calendar, CreditCard, Bell, BellOff, XCircle, Trash2, AlertTriangle, ScrollText
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AdminSettings from '../components/AdminSettings';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { notificationService } from '../services/notificationService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isSubscribed, loading: authLoading, refreshUser, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [originalUsername, setOriginalUsername] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState('');
  
  // Subscription cancellation state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Account deletion state (Apple Guideline 5.1.1(v))
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // Check notification status on mount
  useEffect(() => {
    setNotificationsEnabled(notificationService.isEnabled());
  }, []);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getProfile();
      setProfile(data);
      setUsername(data.username || '');
      setOriginalUsername(data.username || '');
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/profile/billing/subscription-status`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfile();
      fetchSubscriptionStatus();
    }
  }, [isAuthenticated, fetchProfile, fetchSubscriptionStatus]);

  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      const response = await fetch(`${API_URL}/api/profile/billing/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        credentials: 'include',
        body: JSON.stringify({ reason: cancelReason || null })
      });
      
      if (response.ok) {
        const data = await response.json();
        setShowCancelModal(false);
        setCancelReason('');
        setMessage({ 
          type: 'success', 
          text: data.message || 'Subscription will be canceled at the end of your billing period.' 
        });
        // Refresh subscription status
        await fetchSubscriptionStatus();
        await refreshUser();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.detail || 'Failed to cancel subscription' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to cancel subscription. Please try again.' });
    } finally {
      setCancelling(false);
    }
  };

  const handleSaveUsername = async () => {
    if (!username.trim() || username === originalUsername) return;
    
    setSaving(true);
    setMessage({ type: '', text: '' });
    
    try {
      await apiService.setUsername(username.trim());
      setOriginalUsername(username.trim());
      setMessage({ type: 'success', text: 'Username updated successfully!' });
      await refreshUser();
      await fetchProfile();
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Failed to update username' });
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = (type, format) => {
    const url = `${API_URL}/api/user/download/${type}?format=${format}`;
    window.open(url, '_blank');
    setShowDownloadModal(false);
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    if (deleteConfirmText !== 'DELETE') {
      setDeleteError('You must type DELETE (in uppercase) to confirm.');
      return;
    }
    setDeleting(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/profile/account`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          confirmation: deleteConfirmText,
          password: deletePassword || null,
          reason: deleteReason || null,
        }),
      });
      if (response.ok) {
        // Wipe local session and bounce home.
        try { await logout(); } catch { /* ignore — server already wiped session */ }
        localStorage.removeItem('session_token');
        navigate('/', { replace: true });
        return;
      }
      const data = await response.json().catch(() => ({}));
      setDeleteError(data.detail || 'Failed to delete account. Please try again.');
    } catch (e) {
      setDeleteError('Network error — please try again.');
    } finally {
      setDeleting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
          <span className="ml-3 text-gray-400">Loading profile...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]">
      <Header />
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        {/* Profile Header */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-[#3B82F6]/20 to-[#F97316]/20 h-24" />
          <div className="px-6 pb-6">
            <div className="flex items-end gap-4 -mt-10">
              <div className="w-20 h-20 rounded-full bg-[#1e2a45] border-4 border-[#131b2e] flex items-center justify-center overflow-hidden">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <User className="h-10 w-10 text-gray-500" />
                )}
              </div>
              <div className="flex-1 pb-1">
                <h1 className="text-xl font-bold text-white">
                  {profile?.username || user?.name || 'User'}
                </h1>
                <p className="text-gray-500 text-sm">{user?.email}</p>
              </div>
              {isSubscribed && (
                <span className="px-3 py-1 bg-[#3B82F6]/20 text-[#3B82F6] text-sm font-medium rounded-full border border-[#3B82F6]/30">
                  PRO Member
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Username Section */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="h-5 w-5 text-[#3B82F6]" />
            <h2 className="text-lg font-semibold text-white">Username Settings</h2>
          </div>
          
          <p className="text-gray-400 text-sm mb-4">
            Choose a unique username that will be displayed publicly instead of your email.
          </p>
          
          <div className="flex gap-3">
            <div className="flex-1">
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter username (3-30 characters)"
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
                data-testid="username-input"
                maxLength={30}
              />
              <p className="text-xs text-gray-500 mt-1">
                Only letters, numbers, underscores, and hyphens allowed
              </p>
            </div>
            <Button
              onClick={handleSaveUsername}
              disabled={saving || !username.trim() || username === originalUsername}
              data-testid="save-username-btn"
              className="bg-[#3B82F6] hover:bg-[#2563EB] text-white px-6"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
            </Button>
          </div>
          
          {message.text && (
            <div className={`mt-3 flex items-center gap-2 text-sm ${
              message.type === 'success' ? 'text-green-400' : 'text-red-400'
            }`}>
              {message.type === 'success' ? (
                <Check className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              {message.text}
            </div>
          )}
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="h-5 w-5 text-[#F97316]" />
              <span className="text-gray-400 text-sm">Ideas Submitted</span>
            </div>
            <span className="text-2xl font-bold text-white">{profile?.ideas_count || 0}</span>
          </div>
          <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4">
            <div className="flex items-center gap-2 mb-2">
              <Bookmark className="h-5 w-5 text-[#3B82F6]" />
              <span className="text-gray-400 text-sm">Watchlist Items</span>
            </div>
            <span className="text-2xl font-bold text-white">{profile?.watchlist_count || 0}</span>
          </div>
        </div>

        {/* Account Info */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <User className="h-5 w-5 text-[#3B82F6]" />
            <h2 className="text-lg font-semibold text-white">Account Information</h2>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-[#1e2a45]">
              <div className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-gray-500" />
                <span className="text-gray-400 text-sm">Email</span>
              </div>
              <span className="text-white text-sm">{user?.email}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-[#1e2a45]">
              <div className="flex items-center gap-3">
                <Calendar className="h-4 w-4 text-gray-500" />
                <span className="text-gray-400 text-sm">Member Since</span>
              </div>
              <span className="text-white text-sm">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-[#1e2a45]">
              <div className="flex items-center gap-3">
                <CreditCard className="h-4 w-4 text-gray-500" />
                <span className="text-gray-400 text-sm">Subscription</span>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-sm ${isSubscribed ? 'text-green-400' : 'text-gray-400'}`}>
                  {subscriptionStatus?.cancel_at_period_end 
                    ? 'Cancels at period end' 
                    : isSubscribed 
                      ? 'Pro Member' 
                      : 'Free'}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate('/profile/billing')}
                  className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45] text-xs"
                  data-testid="manage-billing-btn"
                >
                  Manage Billing
                </Button>
              </div>
            </div>
            
            {/* Cancel Subscription Section - Only show for subscribed users */}
            {isSubscribed && !profile?.is_admin && (
              <div className="py-4 border-b border-[#1e2a45]">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-white mb-1">Cancel Subscription</h3>
                    {subscriptionStatus?.cancel_at_period_end ? (
                      <p className="text-xs text-yellow-400">
                        Your subscription will end on {subscriptionStatus.expires_at 
                          ? new Date(subscriptionStatus.expires_at).toLocaleDateString() 
                          : 'the end of your billing period'}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">
                        {subscriptionStatus?.days_remaining 
                          ? `${subscriptionStatus.days_remaining} days remaining in current period` 
                          : 'Cancel anytime. Access continues until period end.'}
                      </p>
                    )}
                  </div>
                  {subscriptionStatus?.can_cancel && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowCancelModal(true)}
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                      data-testid="cancel-subscription-btn"
                    >
                      <XCircle className="h-3 w-3 mr-1" />
                      Cancel Subscription
                    </Button>
                  )}
                </div>
              </div>
            )}
            {profile?.is_admin && (
              <div className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <Shield className="h-4 w-4 text-[#F97316]" />
                  <span className="text-gray-400 text-sm">Role</span>
                </div>
                <span className="text-[#F97316] text-sm font-medium">Administrator</span>
              </div>
            )}
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="h-5 w-5 text-[#3B82F6]" />
            <h2 className="text-lg font-semibold text-white">Price Alerts</h2>
          </div>
          
          <p className="text-gray-400 text-sm mb-4">
            Get notified when markets in your watchlist move significantly (5%+ price change).
          </p>
          
          <div className="flex items-center justify-between bg-[#1e2a45]/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              {notificationsEnabled ? (
                <Bell className="h-5 w-5 text-green-400" />
              ) : (
                <BellOff className="h-5 w-5 text-gray-500" />
              )}
              <div>
                <span className="text-white font-medium">Browser Notifications</span>
                <p className="text-xs text-gray-500">
                  {notificationsEnabled ? 'Enabled - You\'ll receive price alerts' : 'Disabled - Enable to receive alerts'}
                </p>
              </div>
            </div>
            <Button
              onClick={async () => {
                if (notificationsEnabled) {
                  setNotificationStatus('Notifications are managed in your browser settings');
                  setTimeout(() => setNotificationStatus(''), 3000);
                } else {
                  const result = await notificationService.requestPermission();
                  setNotificationsEnabled(result);
                  if (result) {
                    // Send test notification
                    await notificationService.showTestNotification();
                    setNotificationStatus('Notifications enabled! Test notification sent.');
                  } else {
                    setNotificationStatus('Permission denied. Check your browser settings.');
                  }
                  setTimeout(() => setNotificationStatus(''), 5000);
                }
              }}
              data-testid="toggle-notifications-btn"
              className={notificationsEnabled 
                ? "bg-green-600 hover:bg-green-700 text-white" 
                : "bg-[#3B82F6] hover:bg-[#2563EB] text-white"}
            >
              {notificationsEnabled ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Enabled
                </>
              ) : (
                <>
                  <Bell className="h-4 w-4 mr-2" />
                  Enable
                </>
              )}
            </Button>
          </div>
          
          {notificationStatus && (
            <div className={`mt-3 text-sm ${notificationsEnabled ? 'text-green-400' : 'text-yellow-400'}`}>
              {notificationStatus}
            </div>
          )}
          
          {notificationsEnabled && (
            <Button
              onClick={async () => {
                const result = await notificationService.showTestNotification();
                setNotificationStatus(result.message);
                setTimeout(() => setNotificationStatus(''), 3000);
              }}
              variant="outline"
              className="mt-3 border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
            >
              Send Test Notification
            </Button>
          )}
        </div>

        {/* Download Data */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Download className="h-5 w-5 text-[#3B82F6]" />
              <h2 className="text-lg font-semibold text-white">Download Your Data</h2>
            </div>
            <Button
              onClick={() => setShowDownloadModal(true)}
              data-testid="download-data-btn"
              className="bg-[#1e2a45] hover:bg-[#2d3a56] text-white"
            >
              <Download className="h-4 w-4 mr-2" />
              Export Data
            </Button>
          </div>
          <p className="text-gray-400 text-sm">
            Download your submitted ideas and watchlist in CSV or TXT format.
          </p>
        </div>

        {/* Danger Zone — Delete Account (Apple Guideline 5.1.1(v)) */}
        <div className="bg-[#131b2e] rounded-2xl border border-red-500/30 p-6 mt-6">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <h2 className="text-lg font-semibold text-white">Danger Zone</h2>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          <ul className="text-xs text-gray-500 list-disc list-inside space-y-1 mb-4">
            <li>Your profile, watchlist, votes, and pending market submissions will be removed.</li>
            <li>Any active subscription will be canceled immediately (no proration).</li>
            <li>Trades on resolved markets remain anonymized to preserve market integrity.</li>
            <li>A short audit record is retained as legally required.</li>
          </ul>
          <Button
            onClick={() => {
              setDeleteConfirmText('');
              setDeletePassword('');
              setDeleteReason('');
              setDeleteError('');
              setShowDeleteModal(true);
            }}
            variant="outline"
            data-testid="open-delete-account-btn"
            className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete My Account
          </Button>
        </div>

        {/* Admin Link */}
        {profile?.is_admin && (
          <>
            <div className="mt-6 space-y-3">
              <Button
                onClick={() => navigate('/admin')}
                data-testid="admin-panel-link"
                className="w-full bg-gradient-to-r from-[#F97316] to-[#EA580C] hover:from-[#FB923C] hover:to-[#F97316] text-white"
              >
                <Shield className="h-4 w-4 mr-2" />
                Open Admin Panel
              </Button>
              <Button
                onClick={() => navigate('/changelog')}
                data-testid="admin-changelog-link"
                variant="outline"
                className="w-full border-[#1e2a45] bg-transparent hover:bg-[#1e2a45]/50 text-white"
              >
                <ScrollText className="h-4 w-4 mr-2" />
                View Changelog
              </Button>
            </div>
            <AdminSettings />
          </>
        )}
      </div>

      <Footer />

      {/* Download Modal */}
      <Dialog open={showDownloadModal} onOpenChange={setShowDownloadModal}>
        <DialogContent className="sm:max-w-md bg-[#050810] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Download className="h-5 w-5 text-[#3B82F6]" />
              Download My Data
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Export your submitted ideas and watchlist
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-[#1e2a45]/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="h-4 w-4 text-[#F97316]" />
                <h4 className="text-white font-medium">My Submitted Ideas</h4>
                <span className="text-xs text-gray-500">({profile?.ideas_count || 0} items)</span>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleDownload('ideas', 'csv')}
                  size="sm"
                  data-testid="download-ideas-csv"
                  className="bg-[#3B82F6] text-white text-xs"
                >
                  CSV
                </Button>
                <Button
                  onClick={() => handleDownload('ideas', 'txt')}
                  size="sm"
                  variant="outline"
                  data-testid="download-ideas-txt"
                  className="border-[#1e2a45] text-gray-300 text-xs"
                >
                  TXT
                </Button>
              </div>
            </div>
            <div className="bg-[#1e2a45]/50 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Bookmark className="h-4 w-4 text-[#3B82F6]" />
                <h4 className="text-white font-medium">My Watchlist</h4>
                <span className="text-xs text-gray-500">({profile?.watchlist_count || 0} items)</span>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleDownload('watchlist', 'csv')}
                  size="sm"
                  data-testid="download-watchlist-csv"
                  className="bg-[#F97316] text-white text-xs"
                >
                  CSV
                </Button>
                <Button
                  onClick={() => handleDownload('watchlist', 'txt')}
                  size="sm"
                  variant="outline"
                  data-testid="download-watchlist-txt"
                  className="border-[#1e2a45] text-gray-300 text-xs"
                >
                  TXT
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Modal */}
      <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
        <DialogContent className="sm:max-w-md bg-[#050810] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-400" />
              Cancel Subscription
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              We're sorry to see you go
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-[#1e2a45]/50 rounded-xl border border-[#1e2a45]">
              <h4 className="text-white font-medium mb-2">What happens when you cancel:</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                  Your subscription will remain active until{' '}
                  <span className="text-white font-medium">
                    {subscriptionStatus?.expires_at 
                      ? new Date(subscriptionStatus.expires_at).toLocaleDateString() 
                      : 'the end of your billing period'}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                  You won't be charged again after that date
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                  Your account and data will be preserved
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                  You can resubscribe anytime
                </li>
              </ul>
            </div>
            
            <div>
              <label className="text-sm text-gray-400 mb-2 block">
                Would you mind telling us why? (optional)
              </label>
              <Textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Help us improve by sharing your feedback..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white resize-none"
                rows={3}
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowCancelModal(false)}
                variant="outline"
                className="flex-1 border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
              >
                Keep Subscription
              </Button>
              <Button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Cancelling...
                  </>
                ) : (
                  'Confirm Cancellation'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Account Modal (Apple Guideline 5.1.1(v)) */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-md bg-[#050810] border-red-500/30" data-testid="delete-account-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-400" />
              Delete Account Permanently
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This action is irreversible. Your data will be removed within minutes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-xs text-red-300 space-y-1">
              <p className="font-medium text-red-400">What gets deleted:</p>
              <ul className="list-disc list-inside ml-1">
                <li>Profile, sessions, push notification subscriptions</li>
                <li>Watchlist, votes, and pending market submissions</li>
                <li>Active Stripe subscription (canceled immediately)</li>
              </ul>
              <p className="pt-1 text-gray-400">
                Trades on resolved markets stay anonymized (legal retention).
              </p>
            </div>

            {profile?.is_subscribed && profile?.subscription_provider === 'apple_iap' && (
              <div
                className="p-3 bg-amber-500/10 border border-amber-500/40 rounded-lg text-xs text-amber-300 space-y-1"
                data-testid="apple-iap-warning"
              >
                <p className="font-medium text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Your subscription is managed by Apple
                </p>
                <p className="text-amber-200/90">
                  Deleting your account will <strong>not</strong> cancel your
                  Apple subscription. To stop future charges, open{' '}
                  <strong>Settings → [your name] → Subscriptions</strong> on
                  your iPhone or iPad and cancel <strong>AMP Predict</strong>{' '}
                  there.
                </p>
              </div>
            )}

            {profile?.email && (
              <p className="text-sm text-gray-400">
                Deleting account for{' '}
                <span className="text-white font-medium">{profile.email}</span>
              </p>
            )}

            <div>
              <label className="text-sm text-gray-300 mb-2 block">
                Type <span className="text-red-400 font-mono font-bold">DELETE</span> to confirm
              </label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
                data-testid="delete-confirm-input"
                className="bg-[#1e2a45] border-[#2d3a56] text-white"
              />
            </div>

            {profile?.has_password && (
              <div>
                <label className="text-sm text-gray-300 mb-2 block">
                  Confirm your password
                </label>
                <Input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Your password"
                  autoComplete="current-password"
                  data-testid="delete-password-input"
                  className="bg-[#1e2a45] border-[#2d3a56] text-white"
                />
              </div>
            )}

            <div>
              <label className="text-sm text-gray-400 mb-2 block">
                Reason (optional, helps us improve)
              </label>
              <Textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                placeholder="Tell us why you're leaving..."
                className="bg-[#1e2a45] border-[#2d3a56] text-white resize-none"
                rows={2}
              />
            </div>

            {deleteError && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-2">
                {deleteError}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => setShowDeleteModal(false)}
                variant="outline"
                disabled={deleting}
                className="flex-1 border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
                data-testid="delete-account-cancel-btn"
              >
                Keep Account
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== 'DELETE'}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-account-confirm-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete Forever'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfilePage;
